import {useCallback, useContext, useEffect, useState} from "react";
import axios from "axios";
import {UserContext} from "./UserContext";
import Chatbot from "./Chatbot";
import {HashLink} from "react-router-hash-link";

const MainPage = () =>{
    const [error,setError] = useState("")
    const [loading,setLoading] = useState("")
    const [phoneNumber,setPhoneNumber] = useState("")
    const [fullName,setFullName] = useState("")
    const [message,setMessage] = useState("")

    const [projects,setProjects] = useState([]);
    const [projectLoading, setProjectLoading] = useState("");
    const [projectError,setProjectError] = useState("")
    const imageUrl = "http://backend.cliffmutinda.com/"
    const [selectedProject,setSelectedProject] = useState(null)
    const {backEndUrl} = useContext(UserContext)

    const MessageSubmit = async (e) =>{
        e.preventDefault()
        setLoading("")
        setError("")
        if(!phoneNumber || !fullName || !message){
            setError("Please fill all the required field!")
            return
        }

        if(phoneNumber.length>15){
            setError("Enter a valid Number!")
            return
        }
        try{
            setLoading("Please wait as we process your request...")
            const MessageInfo = new FormData()
            MessageInfo.append("fullName",fullName)
            MessageInfo.append("phoneNumber",phoneNumber)
            MessageInfo.append("messageReceived",message)

            const response = await axios.post(`${backEndUrl}/receive_message`,MessageInfo)
            setLoading(response.data.message)
            setFullName("")
            setPhoneNumber("")
            setMessage("")
        } catch (error){
            setLoading("")
            setError(error.message)
        }
    }

    const FetchProjects = useCallback(async () =>{
        setProjectLoading("Fetching latest work...")
        try{
            const response = await axios.get(`${backEndUrl}/get_projects`)
            setProjects(response.data.projects)
            setProjectLoading("")
        }catch (error){
            setProjectLoading("")
            setProjectError(error.message)
        }
    },[backEndUrl])

    useEffect(()=>{
        FetchProjects()
    },[FetchProjects])

    const projectCount = projects?.length || 0

    const renderStack = (stackString) =>{
        try{
            const parsed = JSON.parse(stackString || "{}")
            return parsed.techstack || []
        }catch (e){
            return []
        }
    }

    return(
        <div className="page">
            <section className="hero-section" id={"aboutmesection"}>
                <div className="section-container hero-grid">
                    <div className="hero-content">
                        <p className="eyebrow">Full-stack developer & security enthusiast</p>
                        <h1>Designing resilient digital experiences with precision and care.</h1>
                        <p className="body-text">
                            I'm Cliff Mutinda — a builder obsessed with performant frontends, reliable backends,
                            and secure infrastructures. I love translating complex ideas into delightful products
                            for the web and mobile.
                        </p>
                        <div className="hero-tags">
                            <span className="pill">Full-Stack Engineering</span>
                            <span className="pill">Cybersecurity</span>
                            <span className="pill">AI Assistants</span>
                        </div>
                        <div className="cta-group">
                            <HashLink smooth to={"/#projects"} className="btn btn-accent">Explore projects</HashLink>
                            <HashLink smooth to={"/#contacts"} className="btn btn-outline">Let’s talk</HashLink>
                        </div>
                        <div className="hero-stats">
                            <div>
                                <p className="stat-value">{projectCount || "10+"}</p>
                                <p className="stat-label">Projects shipped</p>
                            </div>
                            <div>
                                <p className="stat-value">5+</p>
                                <p className="stat-label">Stacks mastered</p>
                            </div>
                            <div>
                                <p className="stat-value">100%</p>
                                <p className="stat-label">Security-first</p>
                            </div>
                        </div>
                    </div>
                    <div className="hero-visual">
                        <div className="hero-card">
                            <img src="images/introimage.png" alt="Cliff Mutinda" loading="lazy"/>
                            <div className="hero-card-footer">
                                <p>Always iterating, always learning.</p>
                                <span className="pill accent">Available for freelance & collaborations</span>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            <section className="section" id="projects">
                <div className="section-container">
                    <div className="section-header">
                        <p className="eyebrow">Selected work</p>
                        <h2>Personal Projects</h2>
                        <p className="body-text">Each build combines thoughtful UX, scalable architecture, and
                            practical security considerations.</p>
                    </div>

                    {projectLoading && <div className="status-banner info">{projectLoading}</div>}
                    {projectError && <div className="status-banner danger">{projectError}</div>}

                    {projects?.length > 0 ? (
                        <div className="projects-grid">
                            {projects.map((project,index)=>(
                                <article className="project-card" key={index}>
                                    <div className="project-media">
                                        <img src={imageUrl+ project.project_display_image} alt={project.project_name} loading="lazy"/>
                                    </div>
                                    <div className="project-body">
                                        <div className="project-meta">
                                            <p className="project-name">{project.project_name}</p>
                                            <p className="project-summary">{project.project_short_description}</p>
                                        </div>
                                        <div className="project-tags">
                                            {renderStack(project.project_techstack).slice(0,3).map((tech)=>(
                                                <span key={`${project.project_name}-${tech}`} className="chip">{tech}</span>
                                            ))}
                                        </div>
                                        <div className="project-actions">
                                            <button className="btn btn-secondary" onClick={()=>setSelectedProject(project)}>
                                                View details
                                            </button>
                                            <a className="btn btn-ghost" href={project.project_live_link} target="_blank" rel="noopener noreferrer">
                                                Live demo
                                            </a>
                                        </div>
                                    </div>
                                </article>
                            ))}
                        </div>
                    ) : (
                        <div className="empty-state">
                            <p className="empty-title">Projects are loading</p>
                            <p className="body-text">I’m curating a fresh batch of builds. Check back soon!</p>
                        </div>
                    )}
                </div>
            </section>

            <section className="section" id="contacts">
                <div className="section-container contact-layout">
                    <div className="contact-copy">
                        <p className="eyebrow">Let’s collaborate</p>
                        <h2>Bring your next big idea to life</h2>
                        <p className="body-text">
                            I’m open to freelance work, contract roles, and long-term collaborations. Reach out via
                            the form or through any of the channels below.
                        </p>
                        <div className="contact-cards">
                            <div className="contact-card">
                                <p className="label">Call me</p>
                                <p className="contact-value">0799 666 046</p>
                            </div>
                            <div className="contact-card">
                                <p className="label">Email</p>
                                <p className="contact-value">itumocliff@gmail.com</p>
                            </div>
                        </div>
                        <div className="social-links">
                            <a href={"https://github.com/cliff-mutinda"} target={"_blank"} rel={"noopener noreferrer"}>GitHub</a>
                            <a href="https://www.linkedin.com/in/cliff-mutinda-b34979353" target={"_blank"} rel={"noopener noreferrer"}>LinkedIn</a>
                            <a href="https://x.com/cliffmutinda?t=UOqlcWBDjRCfwUDrtv0W9Q&s=08" target="_blank" rel={"noopener noreferrer"}>X</a>
                            <a href="https://wa.me/254769888299" target={"_blank"} rel={"noopener noreferrer"}>WhatsApp</a>
                            <a href="https://t.me/Cliff_Mutinda" target="_blank" rel={"noopener noreferrer"}>Telegram</a>
                        </div>
                    </div>

                    <div className="contact-form-card">
                        <h3>Message me directly</h3>
                        {loading && <div className="status-banner success">{loading}</div>}
                        {error && <div className="status-banner danger">{error}</div>}
                        <form onSubmit={MessageSubmit}>
                            <label className="form-field">
                                <span>Full name</span>
                                <input type="text" value={fullName} onChange={e=>setFullName(e.target.value)} placeholder="Jane Doe" required/>
                            </label>
                            <label className="form-field">
                                <span>Mobile number</span>
                                <input type="text" value={phoneNumber} onChange={e=>setPhoneNumber(e.target.value)} placeholder="07..." required/>
                            </label>
                            <label className="form-field">
                                <span>Message</span>
                                <textarea value={message} onChange={e=>setMessage(e.target.value)} placeholder="What would you like to build?" rows={4} required/>
                            </label>
                            <button type="submit" className="btn btn-accent w-100">Send message</button>
                        </form>
                    </div>
                </div>
            </section>

            {selectedProject && (
                <div className="modal-overlay" role="dialog" aria-modal="true" aria-label="Project details">
                    <div className="modal-panel">
                        <button className="modal-close" onClick={()=>setSelectedProject(null)} aria-label="Close project details">×</button>
                        <div className="modal-body">
                            <img src={imageUrl+ selectedProject.project_display_image} alt={selectedProject.project_name} className="modal-image" loading="lazy"/>
                            <div className="modal-content">
                                <p className="eyebrow">Project</p>
                                <h3>{selectedProject.project_name}</h3>
                                <p className="body-text">{selectedProject.project_description}</p>
                                <div className="tech-stack-section">
                                    <h4 className="tech-stack-heading">Technology Stack</h4>
                                    <div className="modal-tags">
                                        {renderStack(selectedProject.project_techstack).length > 0 ? (
                                            renderStack(selectedProject.project_techstack).map((tech)=>(
                                                <span key={`${selectedProject.project_name}-${tech}`} className="chip">{tech}</span>
                                            ))
                                        ) : (
                                            <span className="chip">Not specified</span>
                                        )}
                                    </div>
                                </div>
                                <div className="modal-actions">
                                    <a className="btn btn-accent" href={selectedProject.project_live_link} target="_blank" rel="noopener noreferrer">
                                        View live build
                                    </a>
                                    <button className="btn btn-ghost" onClick={()=>setSelectedProject(null)}>Close</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            <Chatbot/>
        </div>
    )
}

export default MainPage

